/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Dialog, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  ErrorBox,
  ErrorMessage,
  ErrorTitle,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormCancelButton,
  FormElement,
  FormInputsStack,
  Inputs,
  Validate,
  OutlinedIcons,
  ChipSelectList,
  ChipOption,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { Controller } from 'react-hook-form';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { ViewProps } from './types';

const roleOptions: ChipOption[] = [
  { key: RoleName.ADMIN, label: 'Admin', value: RoleName.ADMIN },
  { key: RoleName.CARE, label: 'Care', value: RoleName.CARE },
  { key: RoleName.DATA, label: 'Data', value: RoleName.DATA },
  { key: RoleName.MARKETING, label: 'Marketing', value: RoleName.MARKETING },
  { key: RoleName.PARTNERSHIPS, label: 'Partnerships', value: RoleName.PARTNERSHIPS },
  { key: RoleName.PHOTO, label: 'Photo', value: RoleName.PHOTO },
];

const {
  OutlinedCheckCircleIcon,
} = OutlinedIcons;

const emailPattern = Validate.emailRegex();

const InviteUserFormView = ({
  control,
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  onCancel,
  onSubmit,
  open,
  register,
  submissionError,
}: ViewProps) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));
  return (
    <Dialog
      fullScreen={matches}
      open={open}
      onClose={onCancel}
    >
      <FormDialogTitle handleClose={onCancel}>
        <Typography
          sx={{
            fontSize: theme.typography.h3,
          }}
        >
          Invite User
        </Typography>
      </FormDialogTitle>
      <FormElement name="invite-user-form" onSubmit={handleSubmit(onSubmit)}>
        <FormDialogContent>
          <FormInputsStack>
            <Inputs.Email
              endAdornment={isValid && (
                <Inputs.Adornment position="end">
                  <OutlinedCheckCircleIcon sx={{ color: theme.palette.success.dark }} />
                </Inputs.Adornment>
              )}
              error={!!errors?.email}
              helperText={errors?.email ? 'Please enter a valid email address' : undefined}
              id="email"
              label="Email"
              required
              {...register('email', { required: true, pattern: emailPattern })}
            />
            <Inputs.Text
              error={!!errors?.givenName}
              helperText={errors?.givenName?.message as string}
              id="givenName"
              label="First Name"
              required
              {...register('givenName', { required: 'Please enter a first name' })}
            />
            <Inputs.Text
              error={!!errors?.familyName}
              helperText={errors?.familyName?.message as string}
              id="familyName"
              label="Last Name"
              required
              {...register('familyName', { required: 'Please enter a last name' })}
            />
            <Controller
              defaultValue={[]}
              control={control}
              name="roles"
              rules={{
                validate: ((value) => value.length > 0) || 'Please select at least one role',
              }}
              render={({ field }) => (
                <ChipSelectList
                  chipProps={errors?.roles ? { sx: { borderColor: 'error.dark', color: 'error.dark' } } : undefined}
                  label="Roles"
                  labelTypographyProps={errors?.roles ? { color: 'error.dark' } : undefined}
                  chipOptions={roleOptions}
                  selectedChips={field.value}
                  onChange={(selectedChips) => {
                    field.onChange(selectedChips);
                  }}
                />
              )}
            />
          </FormInputsStack>
          {submissionError && (
            <ErrorBox>
              <ErrorTitle>
                There was an error on our end.
              </ErrorTitle>
              <ErrorMessage>
                {`Code: ${submissionError.code}: ${submissionError.message}`}
              </ErrorMessage>
            </ErrorBox>
          )}
        </FormDialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={onCancel} />
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Submit
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default InviteUserFormView;
